// "Tap a point on the number line" questions (2026-10-13): the student taps
// one tick of the line in the picture. An answer is sent, and its answer key
// is stored, as ONE string, "p=<value>" (for example "p=0.35", "p=-3",
// "p=5/14"), and the two are marked by comparing those strings. So both
// sides must write a tick's value the same way: canonicalNumberLineValue
// below is the one place that decides how, for a tapped tick and for a key
// alike.
//
// - On a line whose step is a whole number or a decimal: the plain number,
//   with no trailing zeros and "-" for a negative ("0.35", "-3", "2").
// - On a line whose step is a fraction ("1/8"): the value on the line's own
//   bottom number, never simplified, whole numbers included ("5/8", "24/8"
//   for 3, "0/8" for 0).

import { canonicalBoxNumber } from './boxAnswer';
import { readPracticeDiagram } from './practice-diagram-data';
import {
  numberLineDecimal,
  numberLineParts,
  readNumberLine,
  type NumberLineSpec,
} from './practice-number-line-data';

// The one agreed text for a place on a line. `at` is counted in the line's
// parts (see practice-number-line-data.ts), so nothing is rounded.
export function canonicalNumberLineValue(
  line: Pick<NumberLineSpec, 'parts' | 'fraction'>,
  at: number,
): string {
  if (line.fraction) return `${String(at)}/${String(line.parts)}`;
  const plain = numberLineDecimal(line, at);
  return canonicalBoxNumber(plain) ?? plain;
}

// Which tick a value is, counted in the line's parts. The value may be a
// number or text ("0.35", "5/14", "−3"). Null when it is not a tick of the
// line: off the end, between two ticks, or not a number.
export function numberLineTick(
  line: NumberLineSpec,
  value: unknown,
): number | null {
  const at = numberLineParts(line, value);
  if (
    at === null ||
    at < line.min ||
    at > line.max ||
    (at - line.min) % line.step !== 0
  ) {
    return null;
  }
  return at;
}

// The same text from a question's answer as the question bank writes it (a
// number on a whole-number or decimal line, text such as "5/14" on a
// fraction line), for whatever loads the questions. `picture` is the
// question's number line as the bank writes it. It goes through
// canonicalNumberLineValue, so a key and a tapped tick can never be written
// two different ways. Null when the picture is not a number line, or the
// answer is not a tick of it.
export function numberLineKey(
  answer: unknown,
  picture: unknown,
): string | null {
  if (typeof picture !== 'object' || picture === null) return null;
  const record = picture as Record<string, unknown>;
  const line = record.type === 'number_line' ? readNumberLine(record) : null;
  if (line === null) return null;
  const at = numberLineTick(line, answer);
  return at === null ? null : canonicalNumberLineValue(line, at);
}

// The number line a question carries, or null. A tap-a-point question is
// told by its content: it has a number line and none of the lists the other
// formats have. Never tell by the shape of the stored string alone.
export function readNumberLineContent(content: unknown): NumberLineSpec | null {
  if (typeof content !== 'object' || content === null) return null;
  const record = content as Record<string, unknown>;
  if (
    record.options !== undefined ||
    record.boxes !== undefined ||
    record.groups !== undefined ||
    record.items !== undefined ||
    record.accept !== undefined
  ) {
    return null;
  }
  const diagram = readPracticeDiagram(content);
  return diagram?.type === 'number_line' ? diagram : null;
}

// What is sent and stored: "p=0.35".
export function numberLineEntry(canonical: string): string {
  return `p=${canonical}`;
}

// The other way: "p=5/14" back into its value text, "5/14". Null for
// anything else (a several-boxes or sort entry, a tick-all id, for example).
export function parseNumberLineEntry(entry: unknown): string | null {
  if (typeof entry !== 'string') return null;
  const match = /^p=(-?\d+\/\d+|-?\d+(?:\.\d+)?)$/.exec(entry);
  return match?.[1] ?? null;
}
